/**
 * ReportSnapshotViewportsHost — invisible DOM containers for off-screen VTK rendering.
 *
 * Each config in the template gets a dedicated <div> that is kept off-screen and
 * invisible. We use `visibility: hidden` (NOT `display: none`) so that:
 *  - The browser still lays out the element and computes real pixel dimensions.
 *  - WebGL can create a valid canvas context with non-zero width/height.
 *  - The viewports are never visible to the user (z-index: -1 + fixed positioning).
 *
 * The `registerRef` callback is provided by `useReportSnapshotHandler` so the
 * service can locate the container DOM node for each config at capture time.
 */

import type { IReportSnapshotTemplate } from './types';

const DEFAULT_SIZE = { width: 400, height: 400 };

interface Props {
    template: IReportSnapshotTemplate;
    registerRef: (id: string, el: HTMLDivElement | null) => void;
}

export function ReportSnapshotViewportsHost({ template, registerRef }: Props) {
    return (
        <div aria-hidden="true">
            {template.configs.map((config) => {
                const { width, height } = config.size ?? DEFAULT_SIZE;
                return (
                    <div
                        key={config.id}
                        ref={(el) => registerRef(config.id, el)}
                        style={{
                            position: 'absolute',
                            width,
                            height,
                            visibility: 'hidden',
                            overflow: 'hidden',
                        }}
                    />
                );
            })}
        </div>
    );
}
